import classNames from 'classnames'
import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react'
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EyeOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import cn from './index.module.css'
import dayjs from 'dayjs'
import { DatePicker, DatePickerProps, Spin } from 'antd'
import { RangePickerProps } from 'antd/es/date-picker'
import { getAvatar } from '@/helpers/getAvatar'
import { useAddAlert } from '@/hooks/useAddAlert'

export const TextCell = ({
  text = '',
  className = '',
  wrapperClassname = '',
  styles = undefined,
  wrapperStyles = undefined,
  onClick = undefined,
}: {
  text: ReactNode
  wrapperClassname?: string
  className?: string
  styles?: CSSProperties
  wrapperStyles?: CSSProperties
  onClick?: () => void
}) => {
  return (
    <div
      className={classNames(cn.cellWrapper, wrapperClassname)}
      style={wrapperStyles}
    >
      <div
        className={classNames(cn.textCell, className)}
        onClick={onClick}
        style={{
          cursor: onClick != undefined ? 'pointer' : 'default',
          ...styles,
        }}
      >
        {text}
      </div>
    </div>
  )
}

export const UserCell = ({
  name = '',
  icon = '',
  className = '',
  wrapperClassname = '',
  styles = undefined,
  wrapperStyles = undefined,
  onClick = undefined,
}: {
  name: string
  icon?: string | null
  wrapperClassname?: string
  className?: string
  styles?: CSSProperties
  wrapperStyles?: CSSProperties
  onClick?: () => void
}) => {
  return (
    <div
      className={classNames(cn.cellWrapper, wrapperClassname)}
      style={wrapperStyles}
      onClick={onClick}
    >
      <img className={cn.userAvatar} src={getAvatar(icon)} alt={'user photo'} />
      <div
        className={classNames(cn.textCell, className)}
        style={{
          cursor: onClick != undefined ? 'pointer' : 'default',
          ...styles,
        }}
      >
        {name}
      </div>
    </div>
  )
}

export const UserCardCell = ({
  user,
  className = '',
  wrapperClassname = '',
  bodyStyles = undefined,
  wrapperStyles = undefined,
  onClick = undefined,
  hidePersonalData = false,
  stabilizeAvatar = false,
}: {
  user: IUser | null
  wrapperClassname?: string
  className?: string
  bodyStyles?: CSSProperties
  wrapperStyles?: CSSProperties
  onClick?: () => void
  hidePersonalData?: boolean
  stabilizeAvatar?: boolean
}) => {
  const addAlert = useAddAlert()
  const rawAvatarPath = user?.avatar_path as unknown
  const avatarPath = String(
    rawAvatarPath && typeof rawAvatarPath === 'object'
      ? (rawAvatarPath as { path?: string })?.path ?? ''
      : rawAvatarPath ?? '',
  ).trim()
  const isAvatarPathMissing = [
    '',
    'noimg',
    'no-img',
    'no_image',
    'null',
    'undefined',
  ].includes(avatarPath.toLowerCase())
  const fallbackAvatarSrc = getAvatar({ ...user, avatar_path: null })
  const localFallbackAvatarSrc = getAvatar(null)
  const requestedAvatarSrc = getAvatar(
    isAvatarPathMissing
      ? { ...user, avatar_path: null }
      : { ...user, avatar_path: avatarPath },
  )

  const onCopyEmail = async (e?: React.MouseEvent<HTMLElement>) => {
    e?.preventDefault()
    e?.stopPropagation()

    if (!hidePersonalData && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(user?.email ?? '')
      addAlert({ title: 'Скопировано', text: user?.email })
    }
  }

  if (!user) {
    return (
      <div
        className={classNames(cn.userCardCell, wrapperClassname)}
        onClick={onClick}
        style={{
          cursor: onClick != undefined ? 'pointer' : 'default',
          ...wrapperStyles,
        }}
      >
        -
      </div>
    )
  }

  return (
    <div
      className={classNames(cn.userCardCell, wrapperClassname)}
      onClick={onClick}
      style={{
        cursor: onClick != undefined ? 'pointer' : 'default',
        ...wrapperStyles,
      }}
    >
      {stabilizeAvatar ? (
        <span className={cn.userAvatarWrapper}>
          <img
            key={`${user?.id ?? 'unknown'}:${requestedAvatarSrc}`}
            className={classNames(cn.userAvatar, cn.userCardAvatar)}
            src={requestedAvatarSrc}
            onError={(event) => {
              const image = event.currentTarget
              const fallbackSources = [
                fallbackAvatarSrc,
                localFallbackAvatarSrc,
              ].filter(
                (source, index, sources) =>
                  source !== requestedAvatarSrc &&
                  sources.indexOf(source) === index,
              )
              const fallbackIndex = Number(image.dataset.fallbackIndex ?? 0)
              const nextSource = fallbackSources[fallbackIndex]

              if (nextSource) {
                image.dataset.fallbackIndex = String(fallbackIndex + 1)
                image.src = nextSource
                return
              }

              image.onerror = null
            }}
            alt={user?.name ?? ''}
          />
        </span>
      ) : (
        <img
          className={cn.userAvatar}
          src={getAvatar(user?.avatar_path)}
          alt={'user photo'}
        />
      )}
      <div
        className={classNames(cn.userCardBody, className)}
        style={{ ...bodyStyles }}
      >
        <span className={cn.userName}>{user?.name}</span>
        {!hidePersonalData && (
          <span className={cn.userEmail} onClick={onCopyEmail}>
            {user?.email}
          </span>
        )}
      </div>
    </div>
  )
}

export const UserListCell = ({
  users = [],
  className = '',
  wrapperClassname = '',
  styles = undefined,
  wrapperStyles = undefined,
  onClick = undefined,
}: {
  users: IUser[]
  wrapperClassname?: string
  className?: string
  styles?: CSSProperties
  wrapperStyles?: CSSProperties
  onClick?: (user: IUser) => void
}) => {
  return (
    <div
      className={classNames(
        cn.cellWrapper,
        cn.verticalListWrapper,
        wrapperClassname,
      )}
      style={wrapperStyles}
    >
      {users.map((user) => {
        return (
          <div
            key={user.id}
            className={classNames(cn.textCell, className)}
            style={{
              cursor: onClick != undefined ? 'pointer' : 'default',
              textDecoration: onClick != undefined ? 'underline' : 'default',
              ...styles,
            }}
            onClick={() => onClick?.(user)}
          >
            {user?.name ?? '-'}
          </div>
        )
      })}
    </div>
  )
}

export const InputDateCell = ({
  date = '',
  disabledDate = undefined,
  className = '',
  styles = undefined,
  disabled = false,
  onChange,
}: {
  date: string | null
  disabledDate?: RangePickerProps['disabledDate']
  className?: string
  styles?: CSSProperties
  disabled?: boolean
  onChange: (date: string) => void
}) => {
  const [dateValue, setDateValue] = useState<dayjs.Dayjs | null>(
    date ? dayjs(date) : null,
  )
  const onValueChange: DatePickerProps['onChange'] = (date, dateString) => {
    setDateValue(date)
    onChange(dateString)
  }

  return (
    <DatePicker
      className={classNames(cn.cellWrapper, cn.datePickerCell, className)}
      style={styles}
      value={dateValue}
      format="DD.MM.YYYY"
      disabledDate={disabledDate}
      onChange={onValueChange}
      disabled={disabled}
    />
  )
}

export const InputTextAreaCell = ({
  text = '',
  className = '',
  wrapperClassname = '',
  styles = undefined,
  wrapperStyles = undefined,
  disabled = false,
  onChange,
}: {
  text: string
  wrapperClassname?: string
  className?: string
  styles?: CSSProperties
  wrapperStyles?: CSSProperties
  disabled?: boolean
  onChange: (value: string) => void
}) => {
  return (
    <div className={classNames(wrapperClassname)} style={wrapperStyles}>
      <textarea
        className={classNames(cn.cellWrapper, cn.inputTextAreaCell, className)}
        style={styles}
        onChange={(e) => onChange(e?.target?.value)}
        defaultValue={text}
        disabled={disabled}
      />
    </div>
  )
}

export const IconButtonCell = ({
  icon = (
    <CloseOutlined
      onPointerEnterCapture={undefined}
      onPointerLeaveCapture={undefined}
    />
  ),
  className = '',
  onClick = undefined,
}: {
  icon: ReactNode
  className?: string
  onClick?: () => void
}) => {
  return (
    <button
      className={classNames(cn.cellWrapper, cn.iconButtonCell, className)}
      onClick={onClick}
    >
      {icon}
    </button>
  )
}

export const InputTextSaveCell = ({
  type,
  value,
  placeholder,
  onChange,
  onSave,
  onStartEdit,
  className = '',
  styles,
}: {
  type?: 'number' | 'text'
  className?: string
  styles?: CSSProperties
  value: string
  placeholder?: string | null
  onChange: (value: string) => void
  onSave?: () => Promise<boolean>
  onStartEdit?: () => void
}) => {
  const [edit, setEdit] = useState(false)
  const [loading, setLoading] = useState(false)

  const onUpdate = async () => {
    setLoading(true)

    const res = await onSave?.()
    if (res) {
      setEdit(false)
    }

    setLoading(false)
  }

  return (
    <div
      className={classNames(
        cn.inputTextSaveWrapper,
        edit && cn.activeField,
        className,
      )}
    >
      <input
        className={cn.inputText}
        type={type}
        value={value}
        style={styles}
        placeholder={placeholder ?? undefined}
        disabled={!edit}
        onChange={(e) => {
          onChange(e.target.value)
        }}
      />
      {loading ? (
        <Spin
          className={cn.spin}
          indicator={
            <LoadingOutlined
              spin
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          }
          size="small"
        />
      ) : (
        <button
          className={cn.iconButton}
          onClick={async () => {
            if (!edit) {
              if (onStartEdit) {
                onStartEdit()
                return
              }

              setEdit((prev) => !prev)
            } else {
              onUpdate()
            }
          }}
          disabled={loading}
        >
          {edit ? (
            <CheckOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          ) : (
            <EditOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          )}
        </button>
      )}
    </div>
  )
}

export const TextIconButtonCell = ({
  icon = (
    <EyeOutlined
      onPointerEnterCapture={undefined}
      onPointerLeaveCapture={undefined}
    />
  ),
  text,
  onClick,
  className = '',
  wrapperClassname = '',
  hideButton = false,
  wrapperStyles,
  styles,
}: {
  icon?: ReactNode
  className?: string
  wrapperClassname?: string
  wrapperStyles?: CSSProperties
  styles?: CSSProperties
  text: string
  hideButton?: boolean
  onClick?: () => Promise<void>
}) => {
  return (
    <div
      className={classNames(cn.cellWrapper, wrapperClassname)}
      style={wrapperStyles}
    >
      <div
        className={classNames(cn.textCell, className)}
        onClick={onClick}
        style={{
          width: '100%',
          ...styles,
        }}
      >
        {text}
      </div>
      <button
        className={cn.iconButton}
        onClick={onClick}
        disabled={!onClick}
        style={{
          visibility: hideButton || !onClick ? 'hidden' : 'visible',
        }}
      >
        {icon}
      </button>
    </div>
  )
}

export const EditableTextareaCell = ({
  value,
  onSave,
  placeholder = '',
  emptyText = '-',
  className = '',
}: {
  value: string
  onSave: (value: string) => Promise<boolean>
  placeholder?: string
  emptyText?: string
  className?: string
}) => {
  const [edit, setEdit] = useState(false)
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState(value)
  const editorRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setDraft(value)
  }, [value])

  useEffect(() => {
    if (!edit) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        loading ||
        !editorRef.current ||
        editorRef.current.contains(event.target as Node)
      ) {
        return
      }

      setDraft(value)
      setEdit(false)
    }

    document.addEventListener('mousedown', handleClickOutside, true)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [edit, loading, value])

  const openEditor = () => {
    if (loading) {
      return
    }

    setDraft(value)
    setEdit(true)
  }

  const save = async () => {
    setLoading(true)
    const res = await onSave(draft)
    setLoading(false)

    if (res) {
      setEdit(false)
    }
  }

  if (!edit) {
    return (
      <div
        className={classNames(
          cn.cellWrapper,
          cn.editableTextareaPreview,
          className,
        )}
        onClick={openEditor}
      >
        <div className={classNames(cn.textCell, cn.editableTextareaText)}>
          {value || emptyText}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={editorRef}
      className={classNames(
        cn.editableTextareaWrapper,
        cn.activeField,
        className,
      )}
    >
      <textarea
        className={cn.editableTextareaInput}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        disabled={loading}
        autoFocus
      />
      <div className={cn.editableTextareaActions}>
        {loading ? (
          <Spin
            className={cn.spin}
            indicator={
              <LoadingOutlined
                spin
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            }
            size="small"
          />
        ) : (
          <button
            className={cn.editableTextareaSaveButton}
            onClick={save}
            type="button"
          >
            Сохранить
          </button>
        )}
      </div>
    </div>
  )
}
